import React from 'react';
import { Typography as MuiTypography, styled } from '@mui/material';
import type { TypographyProps as MuiTypographyProps } from '@mui/material';
import { alpha } from '@mui/material/styles';

// Custom typography variants
export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'overline'
  | 'button'
  | 'display1'
  | 'display2' // Custom large display variants
  | 'lead' // Custom lead paragraph
  | 'small' // Custom small text
  | 'mono'; // Custom monospace

export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'textPrimary'
  | 'textSecondary'
  | 'textDisabled'
  | 'inherit'
  | 'initial';

export type TypographyWeight = 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | 'black';

export type TypographyAlign = 'left' | 'center' | 'right' | 'justify' | 'inherit';

// Extended props interface
export interface CustomTypographyProps
  extends Omit<MuiTypographyProps, 'variant' | 'color' | 'align'> {
  variant?: TypographyVariant;
  color?: TypographyColor;
  weight?: TypographyWeight;
  align?: TypographyAlign;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  truncate?: boolean | number; // true for single line, number for multiple lines
  gradient?: boolean;
  glow?: boolean;
  underline?: boolean;
  italic?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  capitalize?: boolean;
  noWrap?: boolean;
  gutterBottom?: boolean;
  responsive?: boolean; // Responsive font sizing
  // Internal props for styled component
  customVariant?: TypographyVariant;
  customColor?: TypographyColor;
}

// Styled typography component
const StyledTypography = styled(MuiTypography, {
  shouldForwardProp: prop =>
    ![
      'variant',
      'color',
      'weight',
      'size',
      'truncate',
      'gradient',
      'glow',
      'responsive',
      'customVariant',
      'customColor',
      'underline',
      'italic',
      'uppercase',
      'lowercase',
      'capitalize',
    ].includes(prop as string),
})<CustomTypographyProps>(({
  theme,
  customVariant,
  customColor,
  weight,
  size,
  truncate,
  gradient,
  glow,
  underline,
  italic,
  uppercase,
  lowercase,
  capitalize,
  responsive,
}) => {
  // Font weight mapping
  const weightMap = {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 900,
  };

  // Size modifiers
  const sizeModifiers = {
    xs: 0.75,
    sm: 0.875,
    md: 1,
    lg: 1.125,
    xl: 1.25,
  };

  // Custom variants
  const customVariants = {
    display1: {
      fontSize: responsive ? 'clamp(2.5rem, 4vw, 4rem)' : '4rem',
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    display2: {
      fontSize: responsive ? 'clamp(2rem, 3vw, 3rem)' : '3rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    lead: {
      fontSize: responsive ? 'clamp(1.125rem, 2vw, 1.25rem)' : '1.25rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: theme.palette.text.secondary,
    },
    small: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    mono: {
      fontFamily: '"Fira Code", "Monaco", "Consolas", "Courier New", monospace',
      fontSize: '0.875rem',
      backgroundColor: alpha(theme.palette.grey[500], 0.1),
      padding: theme.spacing(0.25, 0.5),
      borderRadius: theme.spacing(0.5),
    },
  };

  // Color mapping
  const colorMap = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    textPrimary: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    textDisabled: theme.palette.text.disabled,
    inherit: 'inherit',
    initial: 'initial',
  };

  // Base styles
  let styles: React.CSSProperties = {
    ...(weight && { fontWeight: weightMap[weight] }),
    ...(size && { fontSize: `${sizeModifiers[size]}em` }),
    ...(customColor && colorMap[customColor] && { color: colorMap[customColor] }),
    ...(underline && { textDecoration: 'underline' }),
    ...(italic && { fontStyle: 'italic' }),
    ...(uppercase && { textTransform: 'uppercase' }),
    ...(lowercase && { textTransform: 'lowercase' }),
    ...(capitalize && { textTransform: 'capitalize' }),
  };

  // Custom variant styles
  if (customVariant && customVariants[customVariant as keyof typeof customVariants]) {
    styles = {
      ...styles,
      ...customVariants[customVariant as keyof typeof customVariants],
    };
  }

  // Truncation styles
  if (truncate === true) {
    styles = {
      ...styles,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };
  } else if (typeof truncate === 'number' && truncate > 1) {
    styles = {
      ...styles,
      display: '-webkit-box',
      WebkitLineClamp: truncate,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
  }

  // Gradient text
  if (gradient) {
    styles = {
      ...styles,
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    };
  }

  // Glow effect
  if (glow) {
    styles = {
      ...styles,
      textShadow: `0 0 10px ${alpha(customColor ? colorMap[customColor] || theme.palette.primary.main : theme.palette.primary.main, 0.5)}`,
    };
  }

  return styles;
});

export const Typography: React.FC<CustomTypographyProps> = ({
  variant = 'body1',
  color = 'textPrimary',
  weight,
  align = 'inherit',
  size,
  truncate = false,
  gradient = false,
  glow = false,
  underline = false,
  italic = false,
  uppercase = false,
  lowercase = false,
  capitalize = false,
  noWrap = false,
  gutterBottom = false,
  responsive = false,
  children,
  ...props
}) => {
  // Map custom variants to MUI variants or use div for custom variants
  const getMuiVariant = (customVariant: TypographyVariant) => {
    const customVariants = ['display1', 'display2', 'lead', 'small', 'mono'];
    if (customVariants.includes(customVariant)) {
      return undefined; // Use div for custom variants
    }
    return customVariant as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption' | 'button' | 'overline';
  };

  const muiVariant = getMuiVariant(variant);
  const component = muiVariant ? undefined : 'div';

  return (
    <StyledTypography
      {...props}
      variant={muiVariant}
      component={component}
      align={align}
      noWrap={noWrap}
      gutterBottom={gutterBottom}
      // Pass custom props for styling
      customVariant={variant}
      customColor={color}
      weight={weight}
      size={size}
      truncate={truncate}
      gradient={gradient}
      glow={glow}
      underline={underline}
      italic={italic}
      uppercase={uppercase}
      lowercase={lowercase}
      capitalize={capitalize}
      responsive={responsive}
    >
      {children}
    </StyledTypography>
  );
};

// Convenient component variants
export const Heading1: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="h1" {...props} />
);

export const Heading2: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="h2" {...props} />
);

export const Heading3: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="h3" {...props} />
);

export const Heading4: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="h4" {...props} />
);

export const Heading5: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="h5" {...props} />
);

export const Heading6: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="h6" {...props} />
);

export const Display1: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="display1" {...props} />
);

export const Display2: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="display2" {...props} />
);

export const Lead: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="lead" {...props} />
);

export const Body: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="body1" {...props} />
);

export const BodySmall: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="body2" {...props} />
);

export const Caption: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="caption" {...props} />
);

export const Small: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="small" {...props} />
);

export const Code: React.FC<Omit<CustomTypographyProps, 'variant'>> = props => (
  <Typography variant="mono" {...props} />
);

export default Typography;
